import { UserStats, BrainrotAttributes, BrainrotImage } from '../types';

/**
 * 💎 BRAINROT RARITY ENGINE
 * Logic for multi-factor attribute generation and rarity scoring.
 */

const COGNITIVE_DAMAGE_STATES = [
  { max: 4, label: "Stable" },
  { max: 8, label: "Disturbed" },
  { max: 12, label: "Corrupted" },
  { max: 16, label: "Fractured" },
  { max: 19, label: "Ruined" },
  { max: 20, label: "Irrecoverable" }
];

const SIGNAL_PURITY_STATES = [
  { max: 4, label: "Total Noise" },
  { max: 8, label: "Chaotic" },
  { max: 12, label: "Semi-Coherent" },
  { max: 17, label: "Disturbingly Clear" },
  { max: 20, label: "Lucid Rot" }
];

const THOUGHTFORM_TYPES = [
  { score: 3, label: "Dead Meme" },
  { score: 6, label: "Recursive Loop" },
  { score: 9, label: "False Memory" },
  { score: 12, label: "Algorithm Echo" },
  { score: 15, label: "Concept Leak" },
  { score: 18, label: "Reality Error" },
  { score: 20, label: "Forbidden Thought" }
];

const ORIGIN_EVENTS = [
  { score: 2, label: "Normal Generation" },
  { score: 5, label: "First of Day" },
  { score: 8, label: "Streak Completion" },
  { score: 12, label: "Late-Night Generation" },
  { score: 15, label: "Aura Threshold" },
  { score: 18, label: "Seasonal Event" },
  { score: 20, label: "System Anomaly" }
];

const SIGNATURES = [
  { score: 1, label: "Null" },
  { score: 5, label: "Echoed" },
  { score: 9, label: "Inverted" },
  { score: 13, label: "Corrupted Seed" },
  { score: 17, label: "Unstable Hash" },
  { score: 19, label: "Prime Rot" },
  { score: 20, label: "Godmark" }
];

function getLabel(score: number, states: any[]) {
  return states.find(s => score <= (s.max || s.score))?.label || states[states.length - 1].label;
}

export function calculateBrainrotRarity(stats: UserStats): { score: number, tier: any, attributes: BrainrotAttributes } {
  // 1. Cognitive Damage
  const hours = new Date().getHours();
  const isLateNight = hours >= 23 || hours <= 4;
  let cogDamage = Math.floor(Math.random() * 21);
  if (isLateNight) cogDamage = Math.max(cogDamage, Math.floor(Math.random() * 10) + 11);

  // 2. Signal Purity
  let purity = Math.floor(Math.random() * 21);
  if (stats.streak > 5) purity = Math.max(purity, Math.floor(Math.random() * 8) + 12);

  // 3. Thoughtform
  const thoughtformIdx = Math.floor(Math.random() * THOUGHTFORM_TYPES.length);
  const thoughtform = THOUGHTFORM_TYPES[thoughtformIdx];

  // 4. Origin
  let origin = ORIGIN_EVENTS[0];
  if (isLateNight) origin = ORIGIN_EVENTS[3];
  if (Math.random() > 0.98) origin = ORIGIN_EVENTS[6]; // System Anomaly

  // 5. Signature
  let sigRoll = Math.random();
  let signature = SIGNATURES[0];
  if (sigRoll > 0.999) signature = SIGNATURES[6]; // Godmark
  else if (sigRoll > 0.95) signature = SIGNATURES[5]; // Prime
  else if (sigRoll > 0.80) signature = SIGNATURES[4];

  // TOTAL SCORE
  let rawScore = cogDamage + purity + thoughtform.score + origin.score + signature.score;
  
  // Steep Curve above 75
  if (rawScore > 75) {
    const excess = rawScore - 75;
    rawScore = 75 + Math.floor(excess * 0.6);
  }

  // Jitter
  rawScore += (Math.random() > 0.5 ? 1 : -1) * Math.floor(Math.random() * 2);
  const finalScore = Math.max(1, Math.min(100, rawScore));

  // Determine Tier
  let tier: any = 'common';
  if (finalScore >= 95) tier = 'god';
  else if (finalScore >= 81) tier = 'mythic';
  else if (finalScore >= 66) tier = 'legendary';
  else if (finalScore >= 51) tier = 'epic';
  else if (finalScore >= 36) tier = 'rare';
  else if (finalScore >= 21) tier = 'uncommon';

  return {
    score: finalScore,
    tier,
    attributes: {
      cognitive_damage: getLabel(cogDamage, COGNITIVE_DAMAGE_STATES),
      signal_purity: getLabel(purity, SIGNAL_PURITY_STATES),
      thoughtform: thoughtform.label,
      origin_event: origin.label,
      signature: signature.label
    }
  };
}